import React, { useState } from "react";
import {
  createMuiTheme,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  ThemeProvider,
} from "@material-ui/core";
import { BiSort, BiSortUp, BiSortDown } from "react-icons/bi";
import { azAZ } from "@material-ui/core/locale";

const theme = createMuiTheme(azAZ);

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    marginTop: "10px",
    borderRadius: "18px",
    background: "#F5F5F5",
    padding: "0 8px 8px 8px",
  },
  tableRow: {
    borderRadius: "18px",
    border: "unset",
    padding: "16px",
    "&:hover": {
      backgroundColor: "unset !important",
    },
  },
  table: {
    minWidth: 750,
    borderCollapse: "separate",
    borderSpacing: "0 4px",
  },
  tableHeadCell: {
    border: "unset",
    padding: "4px",
    "&:first-child": {
      paddingLeft: "10px",
    },
    "&:last-child": {
      paddingRight: "10px",
    },
  },
  tableCell: {
    border: "unset",
    background: "#FFFFFF",
    marginBottom: "16px",
    padding: "14px",
    "&:first-child": {
      borderRadius: "18px 0 0 18px",
      paddingLeft: "10px",
    },
    "&:last-child": {
      borderRadius: "0 18px 18px 0",
      paddingRight: "10px",
    },
  },
  tableWrapper: {
    overflowX: "auto",
  },
  progresRoot: {
    width: "100%",
    padding: "32px 16px",
  },
  progress: {
    margin: "auto",
    display: "block",
    color: "#e2241b",
  },
}));

export const Icon = ({ sortField, orderBy, fieldName }) => {
  if (sortField === fieldName && orderBy) {
    return <BiSortUp />;
  } else if (sortField === fieldName && !orderBy) {
    return <BiSortDown />;
  } else {
    return <BiSort />;
  }
};

const tableHeadList = [
  {
    id: "startDate",
    label: "Start date",
  },
  {
    id: "endDate",
    label: "End date",
  },
  {
    id: "agentNumber",
    label: "Agent number",
  },
  {
    id: "voucherSales",
    label: "Voucher sales",
  },
  {
    id: "voucherSalesCount",
    label: "Voucher sales count",
  },
  {
    id: "voucherCancels",
    label: "Voucher cancels",
  },
  {
    id: "voucherCancelsCount",
    label: "Voucher cancels count",
  },
  {
    id: "voucherPayments",
    label: "Voucher payments",
  },
  {
    id: "voucherPaymentsCount",
    label: "Voucher payments count",
  },
  {
    id: "voucherBets",
    label: "Voucher bets",
  },
  {
    id: "voucherBetsCount",
    label: "Voucher bets count",
  },
  {
    id: "interimPayments",
    label: "Interim payments",
  },
  {
    id: "interimPaymentsCount",
    label: "Interim payments count",
  },
  {
    id: "withdraws",
    label: "Withdraws",
  },
  {
    id: "withdrawsCount",
    label: "Withdraws count",
  },
  {
    id: "salesCommission",
    label: "Sales commission",
  },
  {
    id: "onlineVoucherSales",
    label: "Online voucher sales",
  },
  {
    id: "onlineVoucherSalesCount",
    label: "Online voucher sales count",
  },
  {
    id: "onlineVoucherBets",
    label: "Online voucher bets",
  },
  {
    id: "onlineVoucherBetsCount",
    label: "Online voucher bets count",
  },
  {
    id: "onlineBetsCommission",
    label: "Online bets commission",
  },
  {
    id: "oldWeekStart",
    label: "Old week start",
  },
  {
    id: "currentBalance",
    label: "Current balance",
  },
];

const format = (value) => {
  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  };
  const formatter = new Intl.DateTimeFormat("az-Az", options);
  if (value) {
    return formatter.format(new Date(value));
  }
  return null;
};

export default function ReportsTable({
  data,
  orderBy,
  setOrderBy,
  sortField,
  setSortField,
  take,
  setSkip,
  setTake,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const classes = useStyles();

  function handleSort(fieldName) {
    const order = fieldName === sortField && !orderBy;
    setSortField(fieldName);
    setOrderBy(order);
  }

  function handleChangePage(e, page) {
    setPage(page);
    const tempSkip = page > 0 ? page * take : 0;
    setSkip(tempSkip);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
    setTake(+event.target.value);
    setPage(0);
    setSkip(0);
  }

  return (
    <div className={classes.root}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {tableHeadList.map(({ id, label }) => (
                <TableCell
                  key={id}
                  className={classes.tableHeadCell}
                  sortDirection={sortField === id ? "asc" : false}
                >
                  <TableSortLabel
                    active={sortField === id}
                    direction={orderBy ? "desc" : "asc"}
                    onClick={() => handleSort(id)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((row, index) => (
              <TableRow key={index} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                  {format(row?.startDate)}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {format(row?.endDate)}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.agentNumber}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherSales}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherSalesCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherCancels}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherCancelsCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherPayments}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherPaymentsCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherBets}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.voucherBetsCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.interimPayments}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.interimPaymentsCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.withdraws}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.withdrawsCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.salesCommission}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.onlineVoucherSales}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.onlineVoucherSalesCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.onlineVoucherBets}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.onlineVoucherBetsCount}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.onlineBetsCommission}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.oldWeekStart}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {row?.currentBalance}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.count > 10 && (
          <ThemeProvider theme={theme}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={data.count}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                "aria-label": "Previous Page",
              }}
              nextIconButtonProps={{
                "aria-label": "Next Page",
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        )}
      </div>
    </div>
  );
}
